import { useEffect, useState } from 'react';

import { getInterestRateFromCBILSAccount } from '../../../multiLoanDashboard/accountsSelector';
import { TitleSectionWrapper } from '../../../pageStructure/titleSectionWrapper/TitleSectionWrapper';
import { MainSectionWrapper } from '../../../pageStructure/mainSectionWrapper';
import { ActionButton } from '../../../ui/actionButton';
import ApprovalStatusLines from './ApprovalStatusLines';
import { MoneyWithDecimals } from '../../../ui/moneyWithDecimals/index';
import { Rate } from '../../../ui/rate/Rate';
import { ActionButtonMakeRepayment } from '../../../multiLoanDashboard/dashboardComponents/actionButtonMakeRepayment';
import { EarlyRepaymentSVG } from '../../../../svgComponents/earlyRepaymentSVG';
import styles from './OutstandingLoanView.module.css';
import { tracking } from '@iwoca/frontend-tracking-library';
import { GetAccountsResponse } from '@iwoca/lapi-client/edge';
import InternationalisedName from '../../../multiLoanDashboard/dashboardComponents/InternationalisedName';
import HelpModal from '../../../navigation/helpModal/HelpModal';
import { Modal } from '@iwoca/orion';
import { SpillBanner } from '../../../spill/SpillBanner';

type Account = NonNullable<GetAccountsResponse['data']>['accounts'][number];

export const OutstandingLoanView = ({ account }: { account: Account }) => {
    const [isModalOpen, setIsModalOpen] = useState(false);
    const isCustomerOverdue = account.loan_status === 'overdue';

    useEffect(() => {
        tracking.ampli.dashboardViewed({
            account_id: account.account_id!,
            product_type: 'cbils',
            dashboard_state: isCustomerOverdue ? 'overdue' : 'ongoing'
        });
    }, [account, isCustomerOverdue]);

    const handleOpenModal = () => {
        setIsModalOpen(!isModalOpen);
    };

    const outstandingBalance = account.pending_balance!.total;
    const approvalStatusPrincipalBalance = account.pending_balance!.principal;
    const interestRate = getInterestRateFromCBILSAccount(account);
    const isEarlyRepaymentEnabled =
        account.repayment_details?.stripe_publishable_key || false;

    return (
        <>
            <div className={styles.bannerContainer}>
                <SpillBanner />
            </div>
            <div data-testid="outstanding-loan-view">
                <TitleSectionWrapper withBackground={true}>
                    <div data-testid="headlineText" className={styles.headline}>
                        Hi <InternationalisedName />, your outstanding balance
                        is{' '}
                        <MoneyWithDecimals
                            value={outstandingBalance}
                            decimalWithSeparatorClassName={
                                styles.decimalsWithSeparator
                            }
                        />
                    </div>

                    <ApprovalStatusLines isPaymentOverdue={isCustomerOverdue} />

                    <div data-testid="credit-outstanding">
                        <span className={styles.amount}>
                            <MoneyWithDecimals
                                decimalWithSeparatorClassName={
                                    styles.decimalsWithSeparator
                                }
                                value={approvalStatusPrincipalBalance}
                            />
                        </span>

                        <div className={styles.description}>
                            Credit outstanding
                        </div>
                        <div>
                            <Rate value={interestRate!} decimalPlaces={2} />{' '}
                            Interest rate per 30 days (interest covered by the
                            government for the first 12 months)
                        </div>
                    </div>
                </TitleSectionWrapper>
                <MainSectionWrapper>
                    {isEarlyRepaymentEnabled ? (
                        <ActionButtonMakeRepayment
                            outstandingBalance={outstandingBalance!}
                            accountId={account.account_id!}
                        />
                    ) : (
                        <ActionButton
                            label="To make an early repayment give us a call"
                            actionIcon={EarlyRepaymentSVG}
                            disabled={false}
                            showIcon={true}
                            onClick={handleOpenModal}
                        />
                    )}
                </MainSectionWrapper>
            </div>
            <Modal isOpen={isModalOpen} onClose={handleOpenModal}>
                <HelpModal />
            </Modal>
        </>
    );
};
